import React, { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import classNames from 'classnames';
import ItemsInCart from '../../../../components/ItemsInCart';
import MarketingOptInCheckbox from '../../../../components/MarketingOptInCheckbox';
import { ReactComponent as ChevronIcon } from '../../../../assets/images/icons/chevron_icon.svg';
import AppPaths from '../../../../AppPaths';
import * as types from '../../../../types';
import * as events from '../../../../lib/analytics/events';
import { useCartPricing, useCartPricingError } from '../../../../contexts/CartPricingContext';
import { useLocale } from '../../../../contexts/LocaleContext';
import useCheckoutV2 from '../../../../hooks/useCheckoutV2';
import { cartContainsPaidBundle } from '../../../../lib/cart';
import CheckoutContext from '../../../../lib/CheckoutContext';
import { formatHardwarePrice } from '../../../../lib/hardwareFirst';
import { showRescuePdpRedesign } from '../../../RescueLanding/RescueBanner';
import PricingSummary from '../Confirm/PricingSummary';
import HardwareFirstCheckoutItems from './HardwareFirstCheckoutItems';
import HardwareFirstCheckoutTotals from './HardwareFirstCheckoutTotals';
import orderStyles from './OrderSummaryColumn.module.scss';

interface OrderSummaryColumnProps {
  selectedShippingOption?: types.IShippingOption;
  defaultExpanded?: boolean;
  /** When set, expanding the mobile drawer tracks `Checkout Funnel > Order Summary Opened`. */
  checkoutFlow?: 'stripe';
  /** Embedded in a page with no cart behind it (the rescue one-page checkout): no Edit cart links. */
  hideEditCart?: boolean;
}

export default function OrderSummaryColumn({
  selectedShippingOption,
  defaultExpanded = false,
  checkoutFlow,
  hideEditCart = false,
}: OrderSummaryColumnProps) {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(defaultExpanded);
  const checkoutV2 = useCheckoutV2();
  const { cart } = useContext(CheckoutContext);
  const products = useSelector((state: types.AppState) => state.config.products);
  // the hardware-first rows know nothing of a membership bundle - no plan, no activation fee, no code
  // taken off the plan - so a rescue order or a paid bundle (e.g. a partner code's 6-month plan) keeps
  // the itemized summary inside the v2 page chrome
  const hardwareFirstRows = checkoutV2 && !showRescuePdpRedesign() && !cartContainsPaidBundle(cart, products);
  const cartPricing = useCartPricing();
  const pricingError = useCartPricingError();
  const locale = useLocale();
  // the same blanks the summary rows use: a dash while pricing has failed, "free" at zero
  const barTotal = pricingError
    ? '\u2014'
    : cartPricing.totalInCents === 0
      ? t('free')
      : formatHardwarePrice(cartPricing.totalInCents, locale);
  // the bar carries the one figure a shopper checks before committing, and what it was before
  // the code and the multi-tracker savings, struck beside it
  const savedInCents =
    Object.values(cartPricing.hardwareDiscounts ?? {}).reduce(
      (sum, discount) => sum + discount.promoDiscountInCents,
      0,
    ) + (cartPricing.hardwareFirstMultiDeviceDiscountInCents ?? 0);

  return (
    <>
      <button
        type="button"
        className={classNames(orderStyles.mobileToggle, { [orderStyles.toggleV2]: checkoutV2 })}
        onClick={() => {
          if (!expanded && checkoutFlow) {
            events.checkoutFunnel.orderSummaryOpened(checkoutFlow);
          }
          setExpanded((v) => !v);
        }}
        aria-expanded={expanded}
      >
        {checkoutV2 ? (
          <>
            <span className={orderStyles.toggleLabel}>{t('hardware_first_cart_order_summary')}</span>
            {savedInCents > 0 && !pricingError && (
              <span className={orderStyles.toggleWas}>
                {formatHardwarePrice(cartPricing.totalInCents + savedInCents, locale)}
              </span>
            )}
            <span className={orderStyles.toggleTotal}>{barTotal}</span>
            <ChevronIcon
              className={classNames(orderStyles.chevron, orderStyles.chevronEnd, {
                [orderStyles.chevronOpen]: expanded,
              })}
            />
          </>
        ) : (
          <>
            <ChevronIcon className={classNames(orderStyles.chevron, { [orderStyles.chevronOpen]: expanded })} />
            <span className={orderStyles.toggleLabel}>{t('order_summary')}</span>
            {!hideEditCart && (
              <Link to={AppPaths.Bag} className={orderStyles.editCartLink} onClick={(e) => e.stopPropagation()}>
                {t('edit_cart')}
              </Link>
            )}
          </>
        )}
      </button>
      <div className={orderStyles.headingRow}>
        <h3 className={orderStyles.desktopHeading}>
          {t(checkoutV2 ? 'hardware_first_cart_order_summary' : 'order_summary')}
        </h3>
        {!hideEditCart && (
          <Link to={AppPaths.Bag} className={orderStyles.editCartLink}>
            {t('edit_cart')}
          </Link>
        )}
      </div>
      <div
        className={classNames(orderStyles.details, {
          [orderStyles.detailsExpanded]: expanded,
          [orderStyles.detailsV2]: checkoutV2,
        })}
      >
        <div className={classNames({ [orderStyles.collapseInner]: checkoutV2 })}>
          <div className={classNames({ [orderStyles.collapseBody]: checkoutV2 })}>
            {checkoutV2 && !hideEditCart && (
              <div className={orderStyles.editRowMobile}>
                <Link to={AppPaths.Bag} className={orderStyles.editCartLink}>
                  {t('edit_cart')}
                </Link>
              </div>
            )}
            {hardwareFirstRows ? <HardwareFirstCheckoutItems /> : <ItemsInCart defaultExpanded={false} />}
            {hardwareFirstRows ? (
              <HardwareFirstCheckoutTotals selectedShippingOption={selectedShippingOption} />
            ) : (
              <PricingSummary selectedShippingOption={selectedShippingOption} />
            )}
            {!checkoutV2 && <MarketingOptInCheckbox />}
          </div>
        </div>
      </div>
    </>
  );
}
